import type { Role } from '@servicetim/shared';
import { useDeleteProjectRole, useProjectRoles } from '@servicetim/ui/api';
import {
  Badge,
  Button,
  type ColumnDef,
  DataTable,
  toast,
  useConfirm,
} from '@servicetim/ui/components';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Section } from '../ui/section';
import { RoleDialog } from './role-dialog';

/** Project roles (system roles badged) with create, rename and delete. */
export function ProjectRolesPanel({ projectId }: { projectId: string }) {
  const roles = useProjectRoles(projectId);
  const remove = useDeleteProjectRole(projectId);
  const confirm = useConfirm();
  const [editing, setEditing] = useState<Role | null | 'new'>(null);

  const onDelete = async (role: Role) => {
    const ok = await confirm({
      title: `Delete role "${role.name}"?`,
      description:
        'Refused while permission grants, field access rules or assignments still reference it.',
      confirmLabel: 'Delete',
      destructive: true,
      onConfirm: () => remove.mutateAsync(role.id),
    });
    if (ok) toast.success(`Role "${role.name}" deleted`);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: handlers and mutation resets are stable for the component lifetime
  const columns = useMemo<ColumnDef<Role>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Role',
        cell: ({ row }) => (
          <span className="inline-flex items-center gap-2 font-medium text-fg">
            {row.original.name}
            {row.original.isSystem ? <Badge variant="secondary">System</Badge> : null}
          </span>
        ),
      },
      {
        accessorKey: 'key',
        header: 'Key',
        cell: ({ getValue }) => <code className="font-mono text-xs">{getValue<string>()}</code>,
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        size: 100,
        cell: ({ row }) => (
          <div className="flex justify-end gap-1">
            <Button
              variant="ghost"
              size="icon"
              aria-label={`Rename ${row.original.name}`}
              onClick={() => setEditing(row.original)}
            >
              <Pencil />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              aria-label={`Delete ${row.original.name}`}
              disabled={row.original.isSystem}
              title={row.original.isSystem ? 'System roles cannot be deleted' : undefined}
              onClick={() => void onDelete(row.original)}
            >
              <Trash2 />
            </Button>
          </div>
        ),
      },
    ],
    [],
  );

  return (
    <Section
      title="Project roles"
      description="System roles exist in every project; add your own for approvers, reviewers or vendors."
      actions={
        <Button size="sm" onClick={() => setEditing('new')}>
          <Plus />
          New role
        </Button>
      }
    >
      <DataTable
        columns={columns}
        data={roles.data}
        getRowId={(r) => r.id}
        isLoading={roles.isPending}
        error={roles.error}
        onRetry={() => void roles.refetch()}
        dense
        caption="Project roles"
      />
      <RoleDialog
        projectId={projectId}
        role={editing === 'new' ? null : editing}
        open={editing !== null}
        onOpenChange={(open) => {
          if (!open) setEditing(null);
        }}
      />
    </Section>
  );
}
